/**
 * apps/command/src/ui/Gerb.tsx — the State Emblem of the Republic of
 * Uzbekistan.
 *
 * ============================================================================
 * THE REAL EMBLEM, NOT A DRAWING OF ONE.
 * ============================================================================
 *
 * This replaces a placeholder that was honest about being one: a gold ring with
 * a dot, inline, with a comment saying "replacing it with the official emblem
 * asset is a P1 task". The asset is the same `gerb.svg` the kiosk has shown to
 * citizens since D-019 — the same file, byte for byte, so the two surfaces
 * cannot drift into showing different emblems.
 *
 * IT IS NOT RECOLOURED, TINTED OR SIMPLIFIED. The emblem is full-colour by law
 * and by design: the Khumo bird, the cotton and wheat, the eight-pointed star,
 * the flag's own green-white-blue. A "dark-theme variant" of a state emblem is
 * not a design decision anybody at this level gets to make, so the file is
 * shown as it is and the panel's dark canvas is what it sits on.
 *
 * ============================================================================
 * IMPORTED, NOT LINKED — AND THAT IS THE LOAD-BEARING PART.
 * ============================================================================
 *
 * `import gerbUrl from '../assets/gerb.svg'` rather than `<img src="/brand/…">`
 * for one specific reason: this app is served at `/markaziy/` (§2.1, and
 * `vite.config.ts` sets `base` to match). A root-absolute path would resolve to
 * the MUROJAAT panel's origin root in production and 404 — while working
 * perfectly in a dev server started at `/`. Vite rewrites an imported asset URL
 * with the configured base, so the emblem cannot go missing only in production.
 *
 * It is a same-origin asset from our own bundle: no CDN, no external request,
 * §15.12 intact.
 *
 * ============================================================================
 * IT IS DECORATION IN THE ACCESSIBILITY TREE, DELIBERATELY.
 * ============================================================================
 *
 * `aria-hidden`, empty `alt`. The emblem always sits beside the institution's
 * NAME as text — «Navoiy shahar hokimligi» — so a screen reader announces the
 * institution once, from the text, rather than twice with one of the readings
 * being a filename.
 */
import gerbUrl from '../assets/gerb.svg';

/**
 * The emblem at a given size.
 *
 * `className` carries the size from the token scale. The SVG's own aspect ratio
 * is 191.453 × 201.48 — TALLER than it is wide — so callers set a HEIGHT and let
 * the width follow (`w-auto`), exactly as the kiosk does per D-019. Setting both
 * would stretch a state emblem, which is the one asset on either surface where
 * that is not merely ugly.
 */
export function Gerb({ className = 'h-7 w-auto' }: { className?: string }): React.JSX.Element {
  return <img src={gerbUrl} alt="" aria-hidden="true" className={className} />;
}
